/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback Action types
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.12.0
 */

import { IOptionItemProps } from '../../../components/common/Select/Select';
import { IActionError, IActionWithPayload } from '../../../modules/app/createAction';
import {
    CASHBACK_GET_ACCOUNT_LIST_FAILURE,
    CASHBACK_GET_ACCOUNT_LIST_START,
    CASHBACK_GET_ACCOUNT_LIST_SUCCESS,
    CASHBACK_GET_META_FAILURE,
    CASHBACK_GET_META_START,
    CASHBACK_GET_META_SUCCESS,
    CASHBACK_SET_ACCOUNT,
} from '../pageCashbackConstants';
import { IPageCashbackReducer } from '../pageCashbackReducer';
import { TCashbackControlActions } from './cashbackControlTypes';

/*
* -------------------------------------------
*            Get Cashback Meta
* -------------------------------------------
*/

export enum ECashbackActions {
    autoSpending = 'CHANGE_SUBJECT_ADDITIONAL_ATTRIBUTE',                 // настройка автоматического резервирования
    reserve = 'REWARD_ACTIVATION',                                        // операция резервирования кэшбэка;
}

export interface ICashbackMeta {
    actions: ECashbackActions[];                        // доступные операции для пользователя
    balance: number;                                    // Баланс кешбэка
    balanceLost?: {                                      // Сгорит до конца месяца
        amount: number;
        lostAfter: string;
    };
    balanceReserved: number;                            // Зарезервировано всего
    isAutoSpendingEnabled: boolean;                     // Статус автоматического резервирования
    isCashbackAvailable: boolean;                       // Подключен ли ЛС к кешбеку
    urlHowToGetCashback: string;                        // Ссылка на информацию о кешбэке для неподключенных ЛС
    urlHowToSpentCashback: string;                      // Ссылка на информацию о кешбэке для подключенных ЛС
    notificationLoyaltyReward?: boolean;                 // Показать информационный блок "Услуги связи за кешбэк"
}

export interface ICashbackGetMetaStartPayload {
    clientId?: string;
}

export type TCashbackGetMetaStartAction =
    IActionWithPayload<typeof CASHBACK_GET_META_START, ICashbackGetMetaStartPayload>;

export type TCashbackGetMetaSuccessPayload = Pick<IPageCashbackReducer, 'meta' | 'accountSelectCurrent'>;
export type TCashbackGetMetaResponse = {
    accountInfo: {
        id: number;
        number: string;
    };
    actions: ECashbackActions[];
    autoSpendingBonusesEnabled: boolean;
    balance: number;
    balanceLost?: {
        amount: number;
        lostAfter: string;
    };
    balanceReserved: number;
    isCashbackAvailable: boolean;
    urlHowToGetCashback: string;
    urlHowToSpentCashback: string;
};

export type TCashbackGetMetaSuccessAction =
    IActionWithPayload<typeof CASHBACK_GET_META_SUCCESS, TCashbackGetMetaSuccessPayload>;

export type TCashbackGetMetaFailureAction = IActionError<typeof CASHBACK_GET_META_FAILURE>;

/*
* -------------------------------------------
*          Cashback Account Select
* -------------------------------------------
*/

export interface ICashbackGetAccountListStartPayload {
    searchValue: string;
    start: boolean;
}

export type TCashbackGetAccountListStartAction =
    IActionWithPayload<typeof CASHBACK_GET_ACCOUNT_LIST_START, ICashbackGetAccountListStartPayload>;

export type TCashbackGetAccountListSuccessPayload =
    Pick<IPageCashbackReducer, 'accountSelectList' | 'accountSelectHasMore'>;
export type TCashbackGetAccountListResponse = Array<{ id: number; number: string }>;
export type TCashbackGetAccountListSuccessAction =
    IActionWithPayload<typeof CASHBACK_GET_ACCOUNT_LIST_SUCCESS, TCashbackGetAccountListSuccessPayload>;

export type TCashbackGetAccountListFailureAction =
    IActionError<typeof CASHBACK_GET_ACCOUNT_LIST_FAILURE>;

export type TCashbackSetAccountAction =
    IActionWithPayload<typeof CASHBACK_SET_ACCOUNT, IOptionItemProps>;

/*
* -------------------------------------------
*           All types for reducer
* -------------------------------------------
*/

export type TPageCashbackActions =
    | TCashbackGetAccountListFailureAction
    | TCashbackGetAccountListStartAction
    | TCashbackGetAccountListSuccessAction
    | TCashbackGetMetaFailureAction
    | TCashbackGetMetaStartAction
    | TCashbackGetMetaSuccessAction
    | TCashbackSetAccountAction
    // tab control actions
    | TCashbackControlActions
    ;
